import Typography from '@mui/material/Typography';
import { Row } from 'further-ui/components/Layout';
import { CashFlowLine, CashFlowStatementRow } from '../types';

type Props = {
  netIncome: CashFlowLine;
  investmentGainLossAdjustment: CashFlowLine;
  nonInvestmentUnrealisedFxAdjustment: CashFlowLine;
  operatingPayablesReceivablesChange: CashFlowLine;
  purchaseOfInvestments: CashFlowLine;
  proceedsFromSaleOfInvestments: CashFlowLine;
  netCashFromOperations: CashFlowLine;
  capitalContributions: CashFlowLine;
  distributions: CashFlowLine;
  capitalDueToFromPartners: CashFlowLine;
  netCashFromFinancing: CashFlowLine;
  netChangeInCash: CashFlowLine;
  cashAtBeginning: CashFlowLine;
  cashAtEnd: CashFlowLine;
};

export default function getRows(data?: Props) {
  const netIncomeRows = [
    {
      id: 'net-income',
      label: 'Net income',
      ...emptyCashFlowLine(data?.netIncome),
    },
  ];
  const netIncomeSubrows: Array<CashFlowStatementRow> = [];

  if (isNonZero(data?.investmentGainLossAdjustment)) {
    netIncomeSubrows.push({
      id: 'realised-and-unrealised-gain-loss-on-investments',
      label: 'Realised and unrealised (gain) / loss on investments',
      ...emptyCashFlowLine(data?.investmentGainLossAdjustment),
    });
  }
  if (isNonZero(data?.nonInvestmentUnrealisedFxAdjustment)) {
    netIncomeSubrows.push({
      id: 'non-investment-unrealised-fx-gain-loss',
      label: 'Non-investment unrealised FX (gain) / loss',
      ...emptyCashFlowLine(data?.nonInvestmentUnrealisedFxAdjustment),
    });
  }
  if (isNonZero(data?.operatingPayablesReceivablesChange)) {
    netIncomeSubrows.push({
      id: 'increase-decrease-in-operating-payables-and-receivables',
      label: 'Increase / (decrease) in operating payables and receivables',
      ...emptyCashFlowLine(data?.operatingPayablesReceivablesChange),
    });
  }
  const purchaseOfInvestments = data?.purchaseOfInvestments;
  if (isNonZero(purchaseOfInvestments)) {
    netIncomeSubrows.push({
      id: 'purchase-of-investments',
      label: 'Purchase of investments',
      ...negateCashFlowLine(purchaseOfInvestments),
    });
  }
  if (isNonZero(data?.proceedsFromSaleOfInvestments)) {
    netIncomeSubrows.push({
      id: 'proceeds-from-sale-of-investments',
      label: 'Proceeds from sale of investments',
      ...emptyCashFlowLine(data?.proceedsFromSaleOfInvestments),
    });
  }

  const netCashFromOperations = emptyCashFlowLine(data?.netCashFromOperations);

  const financingActivityRows: Array<CashFlowStatementRow> = [];

  if (isNonZero(data?.capitalContributions)) {
    financingActivityRows.push({
      id: 'capital-contributions',
      label: 'Capital contributions',
      ...emptyCashFlowLine(data?.capitalContributions),
    });
  }
  if (isNonZero(data?.distributions)) {
    financingActivityRows.push({
      id: 'distributions',
      label: 'Distributions',
      ...emptyCashFlowLine(data?.distributions),
    });
  }
  if (isNonZero(data?.capitalDueToFromPartners)) {
    financingActivityRows.push({
      id: 'capital-due-to-from-partners',
      label: 'Increase / (decrease) in capital due to and from partners',
      ...emptyCashFlowLine(data?.capitalDueToFromPartners),
    });
  }

  const netCashFromFinancing = emptyCashFlowLine(data?.netCashFromFinancing);

  const showFinancingSection =
    financingActivityRows.length > 0 || isNonZero(data?.netCashFromFinancing);

  const summaryRows: Array<CashFlowStatementRow> = [
    {
      id: 'cash-and-cash-equivalents-beginning-of-period',
      label: (
        <Row spacing="xs" centered>
          <Typography variant="h6" component="span" fontSize="inherit">
            Cash and cash equivalents
          </Typography>
          <Typography
            variant="h6"
            component="span"
            fontSize="inherit"
            color="grey.600"
          >
            Start of period
          </Typography>
        </Row>
      ),
      ...emptyCashFlowLine(data?.cashAtBeginning),
    },
    {
      id: 'net-increase-decrease-in-cash-and-cash-equivalents',
      label: 'Net increase / (decrease) in cash and cash equivalents',
      ...emptyCashFlowLine(data?.netChangeInCash),
    },
  ];

  const cashAtEnd = emptyCashFlowLine(data?.cashAtEnd);

  return {
    netIncomeRows,
    netIncomeSubrows,
    netCashFromOperations,
    financingActivityRows,
    netCashFromFinancing,
    showFinancingSection,
    summaryRows,
    cashAtEnd,
  };
}

function negateCashFlowLine(line?: CashFlowLine | null): CashFlowLine {
  if (!line) return { period1: 0, period2: 0, period3: 0 };
  return {
    period1: -(line.period1 ?? 0),
    period2: -(line.period2 ?? 0),
    period3: -(line.period3 ?? 0),
  };
}

function emptyCashFlowLine(d?: CashFlowLine) {
  return d ?? { period1: 0, period2: 0, period3: 0 };
}

function isNonZero(d?: CashFlowLine) {
  if (!d) return false;
  return d.period1 !== 0 || d.period2 !== 0 || d.period3 !== 0;
}
